import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {CplCycle} from '../../../../../model/cpl-cycle.model';
import {CplItem} from '../../../../../model/cpl-item.model';
import { Act48Service } from '../../../../../services/a48.service';
import { CplA48SubmissionDetail } from '../../../../../model/cpl-a48-submission-detail.model';
import { A48UserStatus } from '../../../../../model/enum/a48-user-status.enum';
import { CplUserStatus } from '../../enums/cpl-user-status.enum';
import { CommonValidators, DateHelpers, PortalSettings, PPID_LENGTH, SessionService, User, UserService } from '@edl-development/edl-common';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL } from 'src/app/constants/a48.constants';
import { ComplianceItemHelpersService } from 'src/app/services/compliance-item-helpers/compliance-item-helpers.service';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { combineLatest } from 'rxjs';

@Component({
    templateUrl: './a48-status-modal.component.html',
    styleUrls: ['./a48-status-modal.component.scss']
})
export class Act48StatusModalComponent implements OnInit {
    @Input() userCycle: CplCycle;
    @Input() cplItem: CplItem;
    @Input() canClose: boolean = true;

    @Output() onSubmissionComplete = new EventEmitter<{ success: boolean, ShouldSubmitToPerms: boolean }>();
    
    form: UntypedFormGroup;
    busy: boolean;
    details: CplA48SubmissionDetail;
    portalSettings:PortalSettings;
    isItemComplete:boolean;
    user: User;

    DateHelpers = DateHelpers;
    A48UserStatus = A48UserStatus;
    CplUserStatus = CplUserStatus;
    TimeTrackingType = TimeTrackingTypes;

    constructor(
        public activeModal: NgbActiveModal,
        public a48HelpersService: A48HelpersService,
        private formBuilder: UntypedFormBuilder,
        private act48Service: Act48Service,
        private userService: UserService,
        private sessionService: SessionService,
        private complianceItemHelpersService: ComplianceItemHelpersService
    ) { }

    ngOnInit() {
        this.portalSettings = this.sessionService.PortalSettings;
        this.details = new CplA48SubmissionDetail();
        this.busy = true;
        this.form = this.formBuilder.group({
            PPID: [0, [CommonValidators.isNumericCode(PPID_LENGTH)]],
            Hours: [0, [Validators.required, CommonValidators.isGreaterThanZero(), CommonValidators.isValidDecimal(HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL)]],
            UserDate: ["", []],
            ShouldSubmitToPerms: [true, []]
        });

        combineLatest([
            this.userService.getAccountByUserId(this.userCycle.UserID),
            this.act48Service.getAct48Status(this.userCycle.CycleID)
        ]).subscribe(([user, details]: [User, CplA48SubmissionDetail]) => {
            this.user = user;
            this.details = details;
            this.busy = false;
            this.buildForm();
        }, () => {
            this.busy = false;
        });    
    }

    buildForm(){
        const UserDate = this.generateUserDate(this.details, this.userCycle);
        this.isItemComplete = this.complianceItemHelpersService.isFinalUserStatus(this.userCycle.UserStatus);
        const userDateValidators = this.showSubmittedDate() ? [Validators.required] : [];

        this.form = this.formBuilder.group({
            PPID: [this.details.PPID, this.userCycle.IsPara ? [CommonValidators.isNumericCode(PPID_LENGTH)] : [Validators.required, CommonValidators.isNumericCode(PPID_LENGTH)]],
            Hours: [this.details.Hours, [Validators.required, CommonValidators.isGreaterThanZero(), CommonValidators.isValidDecimal(HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL)]],
            UserDate: [UserDate, userDateValidators],
            SubmissionID: [this.details.SubmissionID, []],
            UserCycleID: [this.userCycle.CycleID, []],
            ShouldSubmitToPerms: [this.details.ShouldSubmitToPerms, []]
        });

        if (!this.showSaveButton()) this.form.disable();
    }

    generateUserDate(details: CplA48SubmissionDetail, userCycle: CplCycle) {
        if (details.UserDate) {
            return DateHelpers.shortDateStringToDate(details.UserDate);
        } else if (!this.DateHelpers.isMinDate(DateHelpers.shortDateStringToDate(userCycle.EndDate))) {
            return DateHelpers.shortDateStringToDate(userCycle.EndDate);
        } else {
            return null;
        }
    }

    onSubmit({ value, valid }: { value: any, valid: boolean}) {
        if (valid) {
            this.busy = true;

            value.UserDate = DateHelpers.DateToShortDate(value.UserDate);
            this.act48Service.OverrideAct48Submission(value)
                .subscribe((response: CplA48SubmissionDetail) => {
                    this.busy = false;
                    this.details = response;
                    this.form.get('SubmissionID').setValue(response.SubmissionID);

                    this.onSubmissionComplete.emit({ success: true, ShouldSubmitToPerms: response.ShouldSubmitToPerms });
                }, error => {
                    this.busy = false;
                    this.onSubmissionComplete.emit({ success: false, ShouldSubmitToPerms: this.userCycle.ShouldSubmitToPerms });
                }); 
        }
    }

    onResubmit({ value, valid }: { value: any, valid: boolean}) {
        if (valid) {
            this.busy = true;
        
            value.UserDate = DateHelpers.DateToShortDate(value.UserDate);
            this.act48Service.ResubmitAct48Submission(value)
                .subscribe((response: CplA48SubmissionDetail) => {
                    this.busy = false;
                    this.details = response;

                    this.onSubmissionComplete.emit({ success: true, ShouldSubmitToPerms: response.ShouldSubmitToPerms });
    
                    if (this.canClose) {
                        this.activeModal.close(true);
                    }
                    
                }, error => {
                    this.busy = false;
                    this.onSubmissionComplete.emit({ success: false, ShouldSubmitToPerms: this.userCycle.ShouldSubmitToPerms });
                });
        }
    }

    showSaveButton() {
        return this.details != null && this.a48HelpersService
            .canSave({StatusID: this.details.StatusID, ParaStatusID: this.details.ParaStatusID, isReadOnly: false, isItemComplete: this.isItemComplete });
    }

    showResubmitButton() {
        return this.details != null && this.a48HelpersService.canResubmit({
            StatusID: this.details.StatusID,
            ParaStatusID: this.details.ParaStatusID,
            isReadOnly: false,
            IsAdminView: true,
            isItemComplete: this.isItemComplete,
            ShouldSubmitToPerms: this.form.get('ShouldSubmitToPerms').value
        });
    }

    showSubmittedDate(){
        return this.cplItem.ItemType == 0 || this.details.StatusID == A48UserStatus.ERROR && this.details.CycleStatusID == CplUserStatus.VERIFIED || this.details.StatusID == A48UserStatus.PENDING;
    }

    cancel() {
        this.activeModal.close();
    }  

    public canDisplaySubmitHoursToggle() {
        return this.a48HelpersService.canDisplaySubmitHoursToggle({
            hasAct48: true,
            isUserPara: this.userCycle?.IsPara,
            hasPPID: !!this.form.get('PPID').value,
            excludeFromSubmission: this.user?.ExcludeFromSubmission,
            contextCode: this.details.ContextCode
        })
    }

    public ensureGreaterThanZero(){
        let cv = this.form.get("Hours").value;
        if(cv < 0) this.form.get("Hours").patchValue(0);
    }

    get ppidControl() {
        return this.form?.get('PPID');
    }

    get hoursControl() {
        return this.form?.get('Hours');
    }

    get userDateControl() {
        return this.form?.get('UserDate');
    }
}

